import React, { createContext, useState, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { getLightTheme, getDarkTheme } from './theme';
import type { ThemeProps } from 'styled-components';
import type { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import PropTypes from 'prop-types';

const getTheme = {
  light: getLightTheme,
  dark: getDarkTheme,
};

type ThemeVariant = keyof typeof getTheme;
export interface BabbelThemeProviderProps extends Partial<ThemeProps<BabbelTheme>> {
  children: ReactNode;
  /**
   * Initialises the provider to some theme variant.
   * Alternatively, the 'system' mode will automatically bind to the currently set operating system/browser variant, or default to `light` if no preference is set.
   *
   * For webview based applications hosted in the Babbel native apps, it is recommended to use 'system'.
   * That way the theming will always match the host application.
   */
  defaultMode?: ThemeVariant | 'system';
}

export const themeContext = createContext({
  toggleMode: () => {},
  currentMode: 'light',
});

const { Provider } = themeContext;

const querySystemPrefersDarkScheme = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)');
    // eslint-disable-next-line no-empty
  } catch {}
};

export const BabbelThemeProvider = ({
  children,
  defaultMode = 'light',
}: BabbelThemeProviderProps) => {
  const [currentMode, setCurrentMode] = useState<ThemeVariant>(
    defaultMode === 'system'
      ? querySystemPrefersDarkScheme()?.matches
        ? 'dark'
        : 'light'
      : defaultMode
  );

  const toggleMode = useCallback(() => {
    if (defaultMode === 'system') {
      console.warn(
        "BabbelThemeProvider: toggleMode is not supported while using the 'system' mode"
      );
    } else {
      setCurrentMode((currentMode) => (currentMode === 'light' ? 'dark' : 'light'));
    }
  }, [defaultMode]);

  useEffect(() => {
    if (defaultMode === 'system') {
      const query = querySystemPrefersDarkScheme();
      const handleChange = (event: MediaQueryListEvent) => {
        setCurrentMode(() => (event.matches ? 'dark' : 'light'));
      };
      query?.addEventListener('change', handleChange);
      return () => {
        query?.removeEventListener('change', handleChange);
      };
    }
  }, [toggleMode, defaultMode]);

  const contextValue = useMemo(
    () => ({
      currentMode,
      toggleMode,
    }),
    [currentMode, toggleMode]
  );

  return (
    <Provider value={contextValue}>
      <ThemeProvider theme={getTheme[currentMode]()}>{children}</ThemeProvider>
    </Provider>
  );
};

BabbelThemeProvider.propTypes = {
  children: PropTypes.node,
  defaultMode: PropTypes.oneOf(['light', 'dark', 'system']),
};
